import {Component} from '@angular/core';
import {ICellRendererAngularComp} from 'ag-grid-angular';
import {ICellRendererParams} from 'ag-grid-enterprise';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {TierDetailSettings} from '@aptitude/shared-vanilla';

import {TierSelectionModalComponent} from './modals/tier-selection-modal.component';
import {Features, Offer} from '../../../../types/types';

interface GridTierSelectorComponentParams extends ICellRendererParams {
  disabled: boolean;
  isEditable: boolean;
  selectedOffer: Offer;
  tierNumber: string;
  attainabilityMixes: any[];
  participantId: number;
  displayOptimized: boolean;
  canChangeTier: boolean;
  isSellerIndividualCommitment: boolean;
  displayAttainability: boolean;
  attainabilityMembers: any[];
  featureList: Features;
}

@Component({
    selector: 'app-grid-tier-selector',
    templateUrl: './grid-tier-selector.component.html',
    styleUrls: ['./grid-tier-selector.component.scss'],
    standalone: false
})
export class GridTierSelectorComponent implements ICellRendererAngularComp {
  params: ICellRendererParams = {} as ICellRendererParams;

  disabled = true;
  displayAttainability = true;
  isEditable = false;
  selectedOffer: any = null;
  attainabilityMixes!: any[];
  participantId!: number;
  tierNumber = '';
  displayOptimized = false;
  canChangeTier = true;
  isSellerIndividualCommitment = false;
  attainabilityMembers!: any[];
  featureList: Features = {};

  constructor(private modalService: NgbModal) {
  }

  refresh(params: ICellRendererParams) {
    return true;
  }

  agInit(params: GridTierSelectorComponentParams) {
    this.params = params || ({} as GridTierSelectorComponentParams);

    this.disabled = params.disabled;
    this.isEditable = params.isEditable;
    this.selectedOffer = params.selectedOffer;
    this.tierNumber = params.value;
    this.attainabilityMixes = params.attainabilityMixes;
    this.participantId = params.participantId;
    this.displayOptimized = params.displayOptimized;
    this.canChangeTier = params.canChangeTier;
    this.isSellerIndividualCommitment = params.isSellerIndividualCommitment;
    this.displayAttainability = params.displayAttainability;
    this.attainabilityMembers = params.attainabilityMembers;
    this.featureList = params.featureList;
  }

  annotateAttainability(
    attainabilityMixes: any,
    participantId: number,
    tiers: any[],
  ) {
    const updatedTiers = tiers.map((tier) => {
      tier.attainability = attainabilityMixes
        .find((participant: any) => participant.buyer.id === participantId)
        .tiers.find(
          (attainabilityTier: any) => attainabilityTier.tier.id === tier.id,
        )
        .offerMixTierResults.find(
          (mix: any) =>
            mix.ignoreAttainability === false &&
            mix.offerMixType === 'OPTIMIZED' &&
            mix.commitmentType === 'INDIVIDUAL_COMMITMENT' &&
            mix.usePrimaryCrosses === true,
        ).attainability;

      return tier;
    });

    return updatedTiers;
  }

  annotateSellerAttainability(
    attainabilityMembers: any,
    participantId: number,
    tiers: any[],
  ) {
    const updatedTiers = tiers.map((tier) => {
      tier.attainability = attainabilityMembers.find(
        (participant: any) =>
          participant.memberId === participantId &&
          participant.tierId === tier.id,
      ).attainability;
      return tier;
    });

    return updatedTiers;
  }

  getTierDetailSettings(offer: any): TierDetailSettings {
    return {
      displayUnorderedTiers:
      offer.unorderedTiers,
      displayUnorderedPricing:
      offer.unorderedPricing,
    };
  }

  displayTierSelector() {
    const tierSelectionModal = this.modalService.open(
      TierSelectionModalComponent,
      {
        ariaLabelledBy: 'modal-basic-title',
      },
    );

    tierSelectionModal.componentInstance.offerName = this.selectedOffer?.name;

    if (this.isSellerIndividualCommitment) {
      tierSelectionModal.componentInstance.tiers =
        this.annotateSellerAttainability(
          this.attainabilityMembers,
          this.participantId,
          // @ts-ignore - structuredClone
          structuredClone(this.selectedOffer?.tiers),
        );
    } else {
      tierSelectionModal.componentInstance.tiers = this.annotateAttainability(
        this.attainabilityMixes,
        this.participantId,
        // @ts-ignore - structuredClone is readily available a couple of ways (it's built into modern browsers and core-js provides a polyfill).
        structuredClone(this.selectedOffer?.tiers),
      );
    }

    tierSelectionModal.componentInstance.tierSelected = this.tierNumber;
    tierSelectionModal.componentInstance.displayOptimized =
      this.displayOptimized;
    tierSelectionModal.componentInstance.canChangeTier = this.canChangeTier;
    tierSelectionModal.componentInstance.displayAttainability =
      this.displayAttainability;
    tierSelectionModal.componentInstance.tierDetailSettings =
      this.getTierDetailSettings(this.selectedOffer);

    tierSelectionModal.result.then(
      (newTier) => {
        // Index into the tiers to get the ID of the new tier we want to switch to.
        const tier = this.selectedOffer?.tiers.find(
          (tier: any) => tier.order === newTier,
        );

        // Set the value of this cell to the newly selected tier ID. This will trigger the valueSetter in the grid for this
        // column.
        const colId = this?.params?.column?.getColId();
        const node = this.params.node;

        if (node && colId) {
          // Row node
          node.setDataValue(colId, tier.id);
        }
      },
      () => {
        // Dismiss, we don't want any error for clicking the X to close the modal.
      },
    );
  }
}
